import { API_ORIGIN } from '../api';
import { PILLARS, type PillarId } from '../constants';
import { navLabel } from '../i18n';
import { useSettings } from '../settings';
import type { RegionSummary } from '../types';
import { dotClass } from '../lib/region';

interface FooterProps {
  regions: RegionSummary[];
  onSelectRegion: (region: string | null) => void;
  onSelect: (pillar: PillarId, subsection: string | null) => void;
}

const API_LINKS = [
  { path: '/api/v1/news', label: 'Published feed' },
  { path: '/api/v1/pillars', label: 'Pillars' },
  { path: '/api/v1/regions', label: 'Desks' },
  { path: '/api/v1/globe-data', label: 'Globe activity (24h)' },
  { path: '/health', label: 'Health' },
];

export default function Footer({ regions, onSelectRegion, onSelect }: FooterProps) {
  const { t, locale } = useSettings();
  const year = new Date().getFullYear();

  return (
    <footer className="mt-8 border-t border-rule bg-surface-raised">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-10 sm:px-6">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="font-serif text-xl font-black tracking-[-0.02em] text-ink">III NEWS</p>
            <p className="mt-2 max-w-[26ch] text-sm leading-relaxed text-ink-muted">
              {t('tagline')}
            </p>
            <p className="mt-3 font-mono text-[9px] uppercase leading-relaxed tracking-[0.14em] text-ink-dim">
              {t('builtWith')}
            </p>
          </div>

          <nav aria-label={t('sections')}>
            <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-dim">
              {t('sections')}
            </p>
            <ul className="space-y-1.5">
              {PILLARS.map((pillar) => (
                <li key={pillar.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(pillar.id, null)}
                    className="flex items-center gap-2 text-sm text-ink-soft hover:text-ink"
                  >
                    <span
                      className="size-1.5"
                      style={{ backgroundColor: pillar.color }}
                      aria-hidden="true"
                    />
                    {navLabel(locale, pillar.id)}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-dim">
              {t('desks')}
            </p>
            <ul className="space-y-1.5">
              {regions.map((region) => (
                <li key={region.region_color}>
                  <button
                    type="button"
                    onClick={() => onSelectRegion(region.region_color)}
                    className="flex items-center gap-2 text-sm text-ink-soft hover:text-ink"
                  >
                    <span className={`size-1.5 ${dotClass(region.region_color)}`} aria-hidden="true" />
                    {region.region}
                    <span className="font-mono text-[9px] text-ink-dim">{region.article_count}</span>
                  </button>
                </li>
              ))}
            </ul>

            <p className="mb-3 mt-5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-dim">
              {t('language')}
            </p>
            <p className="text-sm text-ink-muted">{t('autoTranslated')}</p>
          </div>

          <nav aria-label={t('footerApi')}>
            <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-dim">
              {t('footerApi')}
            </p>
            <ul className="space-y-1.5">
              {API_LINKS.map((link) => (
                <li key={link.path}>
                  <a
                    href={`${API_ORIGIN}${link.path}`}
                    className="font-mono text-[11px] text-ink-soft hover:text-ink"
                  >
                    {link.path}
                  </a>
                </li>
              ))}
            </ul>

            <p className="mb-2 mt-5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-dim">
              {t('footerHow')}
            </p>
            <ol className="space-y-1 text-xs leading-relaxed text-ink-muted">
              <li>{t('how1')}</li>
              <li>{t('how2')}</li>
              <li>{t('how3')}</li>
              <li>{t('how4')}</li>
            </ol>
          </nav>
        </div>

        <div className="mt-10 flex flex-col gap-2 border-t border-rule pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-dim">
            © {year} III NEWS · {t('rights')}
          </p>
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-dim">
            {t('footerNote')}
          </p>
        </div>
      </div>
    </footer>
  );
}
